import React from 'react';
import { ProgramContainer } from '../../entities/formTemplateDataType';
import { getRootComponent } from '../../utilities/ElementUtils';

type CardProps = {
    field
    isEng: boolean
    callbackElement: (programContainer: ProgramContainer) => void
    callbackStepIndex?: (index: number, returnIndex?: number) => void
};

function CardComponent(props: CardProps) {
    const HtmlToReactParser = require('html-to-react').Parser;
    const htmlToReactParser = new HtmlToReactParser();

    const addCard = () => {
        // Safety check: ensure Cards property exists and is a valid array
        if (!props.field.Cards || !Array.isArray(props.field.Cards) || props.field.Cards.length === 0) {
            return null;
        }

        // Set default column number to 2 if not set
        const columnNumber = props.field.CardColumnNumber || 2;
        const columnClass = `ontario-card__container ontario-card--cards-per-row-${columnNumber}`;

        return (
            <ul className={columnClass}>
                {props.field.Cards.map((card, i) => addList(card, i))}
            </ul>
        );
    }

    const handleCardClick = (card) => (event: React.MouseEvent | React.KeyboardEvent) => {
        const redirectUrl = props.isEng ? card.RedirectUrl : card.RedirectUrlFRA;

        if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
        }

        if (card.Script) {
            try {
                // Create the function context object with access to navigation and container
                const functionContext = {
                    callbackStepIndex: props.callbackStepIndex,
                    programContainer: getRootComponent(props.field),
                    card: card,
                    field: props.field,
                    event: event
                };

                // Execute the custom script with the context
                const customFunction = eval(`(${card.Script})`);
                customFunction(functionContext);
            } catch (error) {
                console.error('Error executing card script:', error);
            }
        }   
    };

    const handleKeyDown = (event: React.KeyboardEvent, card: any) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleCardClick(card)(event);
        }
    };

    const addList = (card, index) => {
        const bodyContent = htmlToReactParser.parse(props.isEng ? card.CardbodyContent : card.CardbodyContentFRA);
        const hyperLink = props.isEng ? card.CardHyperlink : card.CardHyperlinkFRA;
        const imageContent = card.imageContent;
        const redirectUrl = props.isEng ? card.RedirectUrl : card.RedirectUrlFRA;
        const hasScript = card.Script && card.Script.trim().length > 0;
        const isClickable = hasScript || (redirectUrl && redirectUrl.trim().length > 0);
        // Layout: "inline" (default) = image beside title, "top" = image above title
        const imageLayout = props.field.cardImageLayout || 'inline';

        if (imageLayout === 'top') {
            // Image on top layout (like service cards)
            // Extract plain text from hyperLink for display, keeping the URL for href
            const hyperlinkText = hyperLink ? hyperLink.replace(/<[^>]*>/g, '') : '';
            const hyperlinkMatch = hyperLink ? hyperLink.match(/href=["']([^"']*)["']/) : null;
            const hyperlinkUrl = hyperlinkMatch ? hyperlinkMatch[1] : (redirectUrl || '#');

            return (
                <li
                    className="ontario-card"
                    onClick={isClickable ? handleCardClick(card) : undefined}
                    style={{ cursor: isClickable ? 'pointer' : 'default', backgroundColor: '#fff' }}
                    key={index} tabIndex={0}
                >
                    {imageContent && (
                        <div className="ontario-card__image-container" dangerouslySetInnerHTML={{ __html: imageContent }} />
                    )}
                    <div className="ontario-card__text-container" style={{ padding: '0.75rem 1rem', backgroundColor: '#fff' }}>
                        {hyperLink && (
                            <h2 className="ontario-card__heading" style={{ margin: '0 0 0.25rem 0' }}>
                                <a href={hyperlinkUrl} style={{ color: '#0066CC', textDecoration: 'underline', fontWeight: 700 }}>
                                    {hyperlinkText}
                                </a>
                            </h2>
                        )}
                        {bodyContent && (
                            <div className="ontario-card__description" style={{ color: '#1a1a1a' }}>
                                <p style={{ margin: 0, color: '#1a1a1a' }}>{bodyContent}</p>
                            </div>
                        )}
                    </div>
                </li>
            );
        }

        // Default inline layout (image beside title)
        return (<li
            className="ontario-card ontario-card--light"
            onClick={isClickable ? handleCardClick(card) : undefined}
            onKeyDown={(e) => handleKeyDown(e, card)}
            style={{ cursor: isClickable ? 'pointer' : 'default' }}
            key={index} tabIndex={0}
        >
            <div style={{ padding: '1rem 1.25rem' }}> 
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', fontSize: '17px' }}>
                    {imageContent && <div style={{ flexShrink: 0, lineHeight: 1 }} dangerouslySetInnerHTML={{ __html: imageContent }} />}
                    {hyperLink && <div style={{ flex: 1, lineHeight: 1 }} dangerouslySetInnerHTML={{ __html: hyperLink }} />}
                </h3>
                {bodyContent && (
                    <div>
                        <p style={{ margin: 0, color: '#1a1a1a' }}>{bodyContent}</p>
                    </div>
                )}
            </div>
        </li>)
    }
    const htmlString = [];

    htmlString.push(addCard());

    return htmlString;
}

export default CardComponent;